import {
  type ClipboardEvent,
  type JSX,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";

type Props = {
  onComplete: (code: string) => void;
  disabled?: boolean;
  /** Change it to clear the boxes, for example after a wrong code. */
  resetKey?: number;
  autoFocus?: boolean;
};

const EMPTY = ["", "", "", "", "", ""];

/** Six boxes for an authenticator code. Paste fills every box; typing moves on; Backspace moves back. */
export default function SixDigitCodeInput({
  onComplete,
  disabled = false,
  resetKey = 0,
  autoFocus = true,
}: Props): JSX.Element {
  const [digits, setDigits] = useState<string[]>(EMPTY);
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    setDigits(EMPTY);
    if (autoFocus) refs.current[0]?.focus();
  }, [resetKey, autoFocus]);

  const isComplete = (values: string[]) => values.every(digit => digit !== "");

  // Submit only on the step from incomplete to complete, so editing a box of a
  // full code does not submit it again.
  const update = (next: string[]) => {
    const wasComplete = isComplete(digits);
    setDigits(next);
    if (!wasComplete && isComplete(next)) onComplete(next.join(""));
  };

  const onChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    if (digit && index < 5) refs.current[index + 1]?.focus();
    update(next);
  };

  const onKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      const next = [...digits];
      next[index - 1] = "";
      setDigits(next);
      refs.current[index - 1]?.focus();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    refs.current[Math.min(pasted.length, 5)]?.focus();
    update(EMPTY.map((_, i) => pasted[i] ?? ""));
  };

  return (
    <div className="flex justify-center gap-2" onPaste={onPaste}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={el => {
            refs.current[i] = el;
          }}
          aria-label={`Digit ${i + 1}`}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={e => onChange(i, e.target.value)}
          onKeyDown={e => onKeyDown(i, e)}
          className="h-14 w-12 rounded-lg border border-gray-300 bg-white text-center text-2xl font-medium shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
        />
      ))}
    </div>
  );
}
